package com.bikompanion.domain.usecase

import dev.zacsweers.metro.Inject
import com.bikompanion.domain.BoundingBox
import com.bikompanion.domain.ParkingCell

/** The most cells one camera move may set off, so a wide view can't queue a dozen loads. */
private const val MaxParkingCellsPerLoad = 6

/**
 * The cells a view covers, the one under the camera first.
 *
 * Trimmed to [MaxParkingCellsPerLoad] rather than abandoned when the view spans more. Bailing
 * out on a wide view meant a window wider than the budget loaded nothing at all — which at the
 * zoom racks start loading from is every desktop window, so the layer stayed empty there at every
 * zoom it was allowed to load at. Covering the middle of the view is a far better answer than
 * covering none of it, and the zoom floor already keeps genuinely continental views out.
 *
 * Ordered by distance from the middle of the view rather than south-west first: the cell the
 * rider is actually looking at should resolve before the corners, and a south-west-first sweep
 * spends its first pick on whatever happens to be in the corner — over a coastal city, the sea.
 *
 * The cell the camera actually sits in is named outright rather than left to that distance to
 * find. A camera parked on a cell corner is exactly equidistant from all four cells meeting
 * there, and which of them a comparison of the squared gaps calls nearest comes down to which
 * way the float noise fell — so the rider could wait on three neighbours they can barely see
 * before the ground under the camera was asked for.
 */
@Inject
class SelectParkingCellsUseCase {
    operator fun invoke(bounds: BoundingBox): List<ParkingCell> {
        val centerLatitude = (bounds.south + bounds.north) / 2
        val centerLongitude = (bounds.west + bounds.east) / 2
        val cameraCell = ParkingCell.containing(centerLatitude, centerLongitude)
        return ParkingCell
            .covering(bounds)
            .sortedWith(
                compareBy(
                    { if (it == cameraCell) 0 else 1 },
                    { cell ->
                        val latitudeGap = cell.centerLatitude - centerLatitude
                        val longitudeGap = cell.centerLongitude - centerLongitude
                        latitudeGap * latitudeGap + longitudeGap * longitudeGap
                    },
                ),
            ).take(MaxParkingCellsPerLoad)
    }
}
